/**
 * Protocol and game constants shared between server and client.
 * The client imports this file directly (single source of truth).
 */

import type { BlockNoiseParams } from './blockNoise.js';
import type { BlockCollisionParams } from './blockCollision.js';

export type {
  BlockNoiseBlendMode,
  BlockNoiseParams,
  BlockNoiseType,
} from './blockNoise.js';
export {
  BLOCK_NOISE_BLEND_MODES,
  BLOCK_NOISE_TYPES,
  DEFAULT_BLOCK_NOISE,
  createNoiseSeed,
  isNoiseActive,
  isValidNoiseSeed,
  parseBlockNoise,
  resolveNoiseSeed,
  withNoiseSeed,
} from './blockNoise.js';

export type {
  BlockCollisionGroupId,
  BlockCollisionParams,
  CellHitbox,
} from './blockCollision.js';
export {
  BLOCK_COLLISION_GROUPS,
  DEFAULT_BLOCK_COLLISION,
  FULL_CELL_HITBOX,
  aabbOverlap,
  collisionJson,
  forLibraryCollision,
  parseBlockCollision,
  resolveCollisionBody,
  tightHitboxFromVoxels,
  transformCellHitbox,
  worldHitboxFromCell,
} from './blockCollision.js';

export const WORLD_SIZE = 512;
export const ZONE_SIZE = 256;
export const WORLD_HEIGHT = 64;
/** The implicit base plate fills y=-19..0 (20 blocks total). */
export const BASE_DEPTH = 20;
export const WORLD_MIN_Y = 1 - BASE_DEPTH;
export const ZONES_PER_WORLD = 4;
export const MAX_BATCH_EDITS = 8192;
export const STANDARD_BLOCK_TYPES = 10;
export const CRAFT_BLOCK_TYPE = 11;
export const LIBRARY_BLOCK_TYPE_BASE = 100_000;
export const CUSTOM_BLOCK_TYPE_BASE = 1_000_000;
export const MAX_CUSTOM_BLOCKS = 1000;
/** Named appearance variants per block definition (geometry / texture / noise / opacity). */
export const MAX_BLOCK_VIEW_SNAPSHOTS = 16;
export const DEFAULT_BLOCK_VIEW_NAME = 'Default';
/**
 * Definition `activeViewId` and placement `viewId` sentinel.
 * - On a definition: Random mode — each new placement picks a snapshot id.
 * - On a placement: follow the definition's current denormalized / animated look.
 * DB encodes Random as `active_view_id IS NULL`.
 */
export const RANDOM_BLOCK_VIEW_ID = 0;
/**
 * Definition `activeViewId` sentinel: play the block's animation for follow
 * placements (`viewId` 0). DB encodes Animation as `active_view_id = 0`.
 */
export const ANIMATION_BLOCK_VIEW_ID = -1;
/** Wall-clock length of one animation tick (20 TPS). */
export const BLOCK_ANIM_TICK_MS = 50;
export const MAX_SEQUENCE_ITEM_TICKS = 60;
export const MAX_SEQUENCE_ITEMS = 64;
/** Default face resolution for new textured cubes; each definition stores its own size. */
export const TEXTURE_GRID_SIZE = 8;
export const TEXTURE_GRID_SIZE_OPTIONS = [4, 8, 16] as const;
export type TextureGridSize = (typeof TEXTURE_GRID_SIZE_OPTIONS)[number];
export const BLOCK_FACE_NAMES = ['east', 'west', 'top', 'bottom', 'south', 'north'] as const;
/** Default craft resolution; definitions store their own size for future controls. */
export const CRAFT_GRID_SIZE = 8;
export const CRAFT_GRID_SIZE_OPTIONS = [4, 8, 16, 32] as const;
/** Per-placement custom-block transform: quarter-turns and axis mirrors. */
export const BLOCK_TRANSFORM_MASK = 0xff;
export const BLOCK_TRANSFORM_MIRROR_X = 1 << 4;
export const BLOCK_TRANSFORM_MIRROR_Y = 1 << 5;
export type BlockTransformAxis = 'x' | 'y' | 'z';

export type BlockTransformOperation =
  | 'rotateHorizontal'
  | 'rotateVertical'
  | 'mirrorHorizontal'
  | 'mirrorVertical';

export function isValidBlockTransform(transform: number): boolean {
  return Number.isInteger(transform) &&
    transform >= 0 &&
    (transform & ~BLOCK_TRANSFORM_MASK) === 0;
}

export function applyBlockTransformOperation(
  transform: number,
  operation: BlockTransformOperation,
  direction: 1 | -1 = 1,
): number {
  switch (operation) {
    case 'rotateVertical':
      return rotateBlockTransform(transform, 'x', direction);
    case 'rotateHorizontal':
      return rotateBlockTransform(transform, 'y', direction);
    case 'mirrorHorizontal':
      return mirrorBlockTransform(transform, 'x');
    case 'mirrorVertical':
      return mirrorBlockTransform(transform, 'y');
  }
}

export function rotateBlockTransform(
  transform: number,
  axis: BlockTransformAxis,
  direction: 1 | -1,
): number {
  const operation = axis === 'x' ? ROTATE_X : axis === 'y' ? ROTATE_Y : ROTATE_Z;
  const matrix = decodeBlockTransform(transform);
  const turns = direction === 1 ? 1 : 3;
  let result = matrix;
  for (let turn = 0; turn < turns; turn++) result = multiplyMatrix(operation, result);
  return encodeBlockTransform(result);
}

export function mirrorBlockTransform(
  transform: number,
  axis: BlockTransformAxis,
): number {
  const operation = axis === 'x' ? MIRROR_X : axis === 'y' ? MIRROR_Y : MIRROR_Z;
  return encodeBlockTransform(
    multiplyMatrix(operation, decodeBlockTransform(transform)),
  );
}

/** Row-major 3x3 orientation matrix for rendering transformed placements. */
export function blockTransformMatrix(transform: number): readonly number[] {
  return decodeBlockTransform(transform);
}

type IntMatrix = readonly number[];
const IDENTITY: IntMatrix = [1, 0, 0, 0, 1, 0, 0, 0, 1];
const ROTATE_X: IntMatrix = [1, 0, 0, 0, 0, -1, 0, 1, 0];
const ROTATE_Y: IntMatrix = [0, 0, 1, 0, 1, 0, -1, 0, 0];
const ROTATE_Z: IntMatrix = [0, -1, 0, 1, 0, 0, 0, 0, 1];
const MIRROR_X: IntMatrix = [-1, 0, 0, 0, 1, 0, 0, 0, 1];
const MIRROR_Y: IntMatrix = [1, 0, 0, 0, -1, 0, 0, 0, 1];
const MIRROR_Z: IntMatrix = [1, 0, 0, 0, 1, 0, 0, 0, -1];

function decodeBlockTransform(transform: number): IntMatrix {
  const marker = isValidBlockTransform(transform) ? transform : 0;
  let matrix = IDENTITY;
  for (let turn = 0; turn < (marker & 0x03); turn++) {
    matrix = multiplyMatrix(ROTATE_X, matrix);
  }
  for (let turn = 0; turn < ((marker >> 2) & 0x03); turn++) {
    matrix = multiplyMatrix(ROTATE_Y, matrix);
  }
  for (let turn = 0; turn < ((marker >> 6) & 0x03); turn++) {
    matrix = multiplyMatrix(ROTATE_Z, matrix);
  }
  if (marker & BLOCK_TRANSFORM_MIRROR_X) matrix = multiplyMatrix(MIRROR_X, matrix);
  if (marker & BLOCK_TRANSFORM_MIRROR_Y) matrix = multiplyMatrix(MIRROR_Y, matrix);
  return matrix;
}

function encodeBlockTransform(matrix: IntMatrix): number {
  const wanted = matrix.join(',');
  for (let marker = 0; marker <= BLOCK_TRANSFORM_MASK; marker++) {
    if (decodeBlockTransform(marker).join(',') === wanted) return marker;
  }
  return 0;
}

function multiplyMatrix(a: IntMatrix, b: IntMatrix): IntMatrix {
  const result = Array<number>(9).fill(0);
  for (let row = 0; row < 3; row++) {
    for (let column = 0; column < 3; column++) {
      for (let index = 0; index < 3; index++) {
        result[row * 3 + column] +=
          a[row * 3 + index] * b[index * 3 + column];
      }
    }
  }
  return result;
}

export function customBlockType(id: number): number {
  return CUSTOM_BLOCK_TYPE_BASE + id;
}

export function libraryBlockType(id: number): number {
  return LIBRARY_BLOCK_TYPE_BASE + id;
}

export function libraryBlockId(type: number): number | null {
  return type >= LIBRARY_BLOCK_TYPE_BASE && type < CUSTOM_BLOCK_TYPE_BASE
    ? type - LIBRARY_BLOCK_TYPE_BASE
    : null;
}

export function customBlockId(type: number): number | null {
  return type >= CUSTOM_BLOCK_TYPE_BASE ? type - CUSTOM_BLOCK_TYPE_BASE : null;
}

export type CollectionAccess = 'private' | 'friends' | 'public';

/** Virtual built-in collection shared by every player; not stored per user. */
export const CORE_COLLECTION_ID = 0;

export const CORE_COLLECTION: BlockCollection = {
  id: CORE_COLLECTION_ID,
  name: 'Core',
  description: 'Basic blocks shared by every player. This collection cannot be edited.',
  access: 'public',
};

/** Built-in inventory types that belong to the Core collection. */
export const CORE_BLOCK_TYPES = [CRAFT_BLOCK_TYPE] as const;

export function isCoreCollectionId(id: number | null | undefined): boolean {
  return id === CORE_COLLECTION_ID;
}

export function isCoreBlockType(type: number): boolean {
  return (CORE_BLOCK_TYPES as readonly number[]).includes(type);
}

export interface BlockCollection {
  id: number;
  name: string;
  description: string;
  access: CollectionAccess;
  /**
   * Shared collections from market/trade/friends that the player did not create.
   * External collections are read-only: no edits to the collection or its blocks.
   */
  external?: boolean;
}

/** Lightweight ref for switching block appearance snapshots in inventory. */
export interface BlockViewSnapshotRef {
  id: number;
  name: string;
}

/** One step in a sequence / parametric animation; `ticks` 0 skips the item. */
export interface SequenceAnimationItem {
  viewId: number;
  ticks: number;
}

/**
 * Discrete snapshot frames over time (complex textured animations).
 * Each item holds that snapshot for `ticks` before advancing.
 */
export interface BlockSequenceAnimation {
  type: 'sequence';
  /**
   * When true, all placements share the world ticker (`worldTick % period`).
   * When false, each placement uses `(worldTick - animOrigin) % period`.
   */
  sync: boolean;
  items: SequenceAnimationItem[];
}

/**
 * Smooth parameter blend between snapshot keyframes (solid-fill textured + noise).
 * Each item's `ticks` is the duration of the lerp from that snapshot to the next
 * (wrapping from the last item back to the first).
 */
export interface BlockParametricAnimation {
  type: 'parametric';
  sync: boolean;
  items: SequenceAnimationItem[];
}

/** Anchor in unit-cube space [0,1]³ for custom-geometry oscillators. */
export const OSCILLATOR_ANCHORS = [
  'center',
  'bottom',
  'top',
  'north',
  'south',
  'east',
  'west',
] as const;
export type OscillatorAnchor = (typeof OSCILLATOR_ANCHORS)[number];

export const OSCILLATOR_MODES = ['skew', 'rotate', 'scale'] as const;
export type OscillatorMode = (typeof OSCILLATOR_MODES)[number];

/** Single-axis deformation direction (use multiple oscillators for multi-axis). */
export const OSCILLATOR_AXES = ['x', 'y', 'z'] as const;
export type OscillatorAxis = (typeof OSCILLATOR_AXES)[number];

export const MAX_OSCILLATORS = 8;
export const MAX_OSCILLATOR_PERIOD_TICKS = 60;
/** Amplitude as a fraction of block size (0..1). */
export const MAX_OSCILLATOR_AMPLITUDE = 1;

export interface BlockOscillator {
  mode: OscillatorMode;
  /** World-axis the mode acts along (skew offset / rotate-around / scale). */
  axis: OscillatorAxis;
  anchor: OscillatorAnchor;
  /** Oscillation period in ticks (1..MAX_OSCILLATOR_PERIOD_TICKS). */
  period: number;
  /**
   * Starting phase offset in ticks (0..MAX_OSCILLATOR_PERIOD_TICKS).
   * Added to the animation clock before wrapping by period.
   */
  phaseOffset: number;
  /** Peak displacement scale 0..1 (grows linearly with distance from anchor). */
  amplitude: number;
}

/**
 * Visual-only voxel deformation for custom geometry (skew / rotate / scale).
 * Multiple oscillators compose; hitboxes stay the rest-pose cell.
 */
export interface BlockOscillatorAnimation {
  type: 'oscillator';
  sync: boolean;
  oscillators: BlockOscillator[];
}

export type BlockAnimation =
  | BlockSequenceAnimation
  | BlockParametricAnimation
  | BlockOscillatorAnimation;

export const DEFAULT_BLOCK_ANIMATION: BlockSequenceAnimation = {
  type: 'sequence',
  sync: false,
  items: [],
};

export const DEFAULT_OSCILLATOR: BlockOscillator = {
  mode: 'skew',
  axis: 'x',
  anchor: 'bottom',
  period: 20,
  phaseOffset: 0,
  amplitude: 0.15,
};

/** Full custom appearance payload for one named snapshot (world random placements). */
export interface CustomBlockViewSnapshot {
  id: number;
  name: string;
  gridSize: number;
  voxels: Array<[number, VoxelMaterial]>;
  geometry: CustomBlockCuboid[];
  noise: BlockNoiseParams | null;
}

/** Full library appearance payload for one named snapshot. */
export type LibraryBlockViewSnapshot =
  | {
      id: number;
      name: string;
      kind: 'solid';
      color: string;
      opacity: number;
      /** Null / omitted = no glow. 0..1 when glowing. */
      glowIntensity: number | null;
    }
  | {
      id: number;
      name: string;
      kind: 'textured';
      color: string;
      opacity: number;
      gridSize: TextureGridSize;
      pixels: TexturePixels;
      noise: BlockNoiseParams | null;
    };

export interface CustomBlockDefinition {
  id: number;
  name: string;
  gridSize: number;
  /** Sparse micro-voxels as [linearIndex, standardBlockType or #rrggbb]. */
  voxels: Array<[number, VoxelMaterial]>;
  /** Maximal same-material cuboids generated from voxels for world rendering. */
  geometry: CustomBlockCuboid[];
  /**
   * Optional non-destructive visual noise. Original voxel materials stay
   * unchanged; clients apply noise at render / icon time.
   */
  noise: BlockNoiseParams | null;
  /** Player collision / hitbox settings. */
  collision: BlockCollisionParams;
  /** Null when the block is not assigned to a user collection. */
  collectionId: number | null;
  /** Named view snapshots; active fields above mirror `activeViewId` when not random. */
  views: BlockViewSnapshotRef[];
  /** Full snapshot payloads (same ids as `views`) for placement rendering. */
  snapshots: CustomBlockViewSnapshot[];
  /**
   * `RANDOM_BLOCK_VIEW_ID` (0) = pick a random snapshot when placing.
   * `ANIMATION_BLOCK_VIEW_ID` (-1) = play `animation` for follow placements.
   */
  activeViewId: number;
  animation: BlockAnimation;
}

/**
 * Standard type id, `#rrggbb` / `#rrggbbaa`, or `#rrggbbaagg` (alpha + glow).
 * Glow byte `gg` is 0..255; omitted or 00 means no glow.
 */
export type VoxelMaterial = number | string;

/** Pack solid paint into a voxel material (#rrggbbaa or #rrggbbaagg). */
export function packVoxelMaterial(
  color: string,
  opacity: number,
  glowIntensity: number | null = null,
): string {
  const hex = color.trim().toLowerCase();
  const base = hex.length >= 7 ? hex.slice(0, 7) : '#ffffff';
  const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255)
    .toString(16)
    .padStart(2, '0');
  const glow = glowIntensity == null || glowIntensity <= 0
    ? 0
    : Math.round(Math.max(0, Math.min(1, glowIntensity)) * 255);
  if (glow <= 0) return `${base}${alpha}`;
  return `${base}${alpha}${glow.toString(16).padStart(2, '0')}`;
}

/** Opacity 0..1 from a hex voxel material (numbers are opaque). */
export function voxelMaterialOpacity(material: VoxelMaterial): number {
  if (typeof material === 'number') return 1;
  if (material.length >= 9) {
    return Number.parseInt(material.slice(7, 9), 16) / 255;
  }
  return 1;
}

/** Glow intensity 0..1 from a hex voxel material (0 when absent). */
export function voxelMaterialGlow(material: VoxelMaterial): number {
  if (typeof material === 'number') return 0;
  if (material.length >= 11) {
    return Number.parseInt(material.slice(9, 11), 16) / 255;
  }
  return 0;
}

export function isVoxelGlowing(material: VoxelMaterial): boolean {
  return voxelMaterialGlow(material) > 0;
}

export interface CustomBlockCuboid {
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  depth: number;
  material: VoxelMaterial;
}

export function optimizeCustomBlockVoxels(
  voxels: Array<[number, VoxelMaterial]>,
  gridSize: number,
): CustomBlockCuboid[] {
  const cells = new Map<number, VoxelMaterial>(voxels);
  const used = new Set<number>();
  const cuboids: CustomBlockCuboid[] = [];
  const indexAt = (x: number, y: number, z: number) =>
    x + z * gridSize + y * gridSize * gridSize;
  const matches = (x: number, y: number, z: number, material: VoxelMaterial) => {
    const index = indexAt(x, y, z);
    return !used.has(index) && cells.get(index) === material;
  };

  for (let y = 0; y < gridSize; y++) {
    for (let z = 0; z < gridSize; z++) {
      for (let x = 0; x < gridSize; x++) {
        const index = indexAt(x, y, z);
        const material = cells.get(index);
        if (material === undefined || used.has(index)) continue;

        let width = 1;
        while (x + width < gridSize && matches(x + width, y, z, material)) width++;
        let depth = 1;
        depthLoop: while (z + depth < gridSize) {
          for (let dx = 0; dx < width; dx++) {
            if (!matches(x + dx, y, z + depth, material)) break depthLoop;
          }
          depth++;
        }
        let height = 1;
        heightLoop: while (y + height < gridSize) {
          for (let dz = 0; dz < depth; dz++) {
            for (let dx = 0; dx < width; dx++) {
              if (!matches(x + dx, y + height, z + dz, material)) {
                break heightLoop;
              }
            }
          }
          height++;
        }
        for (let dy = 0; dy < height; dy++) {
          for (let dz = 0; dz < depth; dz++) {
            for (let dx = 0; dx < width; dx++) {
              used.add(indexAt(x + dx, y + dy, z + dz));
            }
          }
        }
        cuboids.push({ x, y, z, width, height, depth, material });
      }
    }
  }
  return cuboids;
}

export type TexturePixels = string[][];

export function isValidTextureGridSize(value: number): value is TextureGridSize {
  return (TEXTURE_GRID_SIZE_OPTIONS as readonly number[]).includes(value);
}

/** Infer face size from six equal square face arrays; null if invalid. */
export function textureGridSizeFromPixels(pixels: TexturePixels): TextureGridSize | null {
  if (!Array.isArray(pixels) || pixels.length !== BLOCK_FACE_NAMES.length) return null;
  const count = pixels[0]?.length;
  if (!count) return null;
  const size = Math.round(Math.sqrt(count));
  if (size * size !== count || !isValidTextureGridSize(size)) return null;
  if (!pixels.every((face) => Array.isArray(face) && face.length === count)) return null;
  return size;
}

/** Max concurrent omnidirectional glow point lights (nearest to camera). */
export const MAX_ACTIVE_GLOW_LIGHTS = 8;
/** Ignore glow sources farther than this many blocks from the camera. */
export const GLOW_LIGHT_CULL_DISTANCE = 28;
/** PointLight.distance at glow intensity 1.0 (scales down with intensity). */
export const GLOW_LIGHT_RANGE = 10;
/** PointLight.intensity multiplier for glow 0..1. */
export const GLOW_LIGHT_INTENSITY_SCALE = 1.25;

export type LibraryBlockDefinition =
  | {
      id: number;
      kind: 'solid';
      name: string;
      color: string;
      opacity: number;
      /**
       * Null = not a glowing block. 0..1 enables an unshadowed point light
       * and self-lit mesh rendering. Solid glow may be painted into custom
       * geometry voxels; it is not used in textured face pixels.
       */
      glowIntensity: number | null;
      collision: BlockCollisionParams;
      collectionId: number | null;
      views: BlockViewSnapshotRef[];
      snapshots: LibraryBlockViewSnapshot[];
      activeViewId: number;
      animation: BlockAnimation;
    }
  | {
      id: number;
      kind: 'textured';
      name: string;
      color: string;
      opacity: number;
      /** Face resolution (and craft paint grid); 8 by default, 16 when uploading 16² faces. */
      gridSize: TextureGridSize;
      pixels: TexturePixels;
      textureUrl: string;
      /**
       * Optional non-destructive visual noise. Source face pixels stay unchanged;
       * clients apply noise when building materials / icons.
       */
      noise: BlockNoiseParams | null;
      collision: BlockCollisionParams;
      collectionId: number | null;
      views: BlockViewSnapshotRef[];
      snapshots: LibraryBlockViewSnapshot[];
      activeViewId: number;
      animation: BlockAnimation;
    };

/** Zone quadrant containing the given block/world coordinates. */
export function zoneIndexAt(x: number, z: number): number {
  return (x >= ZONE_SIZE ? 1 : 0) + (z >= ZONE_SIZE ? 2 : 0);
}

export const ZONE_NAMES = ['North-West', 'North-East', 'South-West', 'South-East'];

export interface SkinDef {
  id: string;
  name: string;
}

export const SKINS: readonly SkinDef[] = [
  { id: 'male_a', name: 'Max' },
  { id: 'male_b', name: 'Leo' },
  { id: 'female_a', name: 'Mia' },
  { id: 'female_b', name: 'Zoe' },
];

export const DEFAULT_SKIN = 'male_a';

export interface PlayerState {
  id: string;
  nick: string;
  skin: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export interface ZoneState {
  index: number;
  ownerId: string | null;
  ownerNick: string | null;
}

export type ClientMsg =
  | { t: 'move'; x: number; y: number; z: number; yaw: number; pitch: number }
  | { t: 'place'; x: number; y: number; z: number; type: number }
  | { t: 'remove'; x: number; y: number; z: number }
  | { t: 'batch'; edits: BlockEditTuple[] }
  | { t: 'claim'; zone: number }
  | { t: 'free'; zone: number };

/**
 * Sparse override as [x, y, z, type, transform, viewId, animOrigin].
 * `viewId` 0 follows the definition's active / animated look; >0 pins a snapshot.
 * `animOrigin` is the world tick when the cell was placed (unsynced animation).
 * Stored as BIGINT — wall-clock ticks (`Date.now()/50`) exceed INT UNSIGNED.
 */
export type BlockTuple = [number, number, number, number, number, number, number];
/** Desired absolute cell state, used by mass build, transforms, and history. */
export type BlockEditTuple = [number, number, number, number, number, number, number];

export function placementViewId(raw: unknown): number {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : RANDOM_BLOCK_VIEW_ID;
}

export function placementAnimOrigin(raw: unknown): number {
  const value = Number(raw);
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

/** Wall-clock world animation tick (shared across clients via Date.now). */
export function currentWorldAnimTick(nowMs = Date.now()): number {
  return Math.floor(nowMs / BLOCK_ANIM_TICK_MS);
}

/** DB `active_view_id`: NULL → Random, 0 → Animation, >0 → snapshot. */
export function decodeActiveViewId(stored: number | null | undefined): number {
  if (stored == null || !Number.isInteger(stored)) return RANDOM_BLOCK_VIEW_ID;
  if (stored === 0) return ANIMATION_BLOCK_VIEW_ID;
  return stored > 0 ? stored : RANDOM_BLOCK_VIEW_ID;
}

/** Encode protocol activeViewId for DB `active_view_id`. */
export function encodeActiveViewId(activeViewId: number): number | null {
  if (activeViewId === RANDOM_BLOCK_VIEW_ID) return null;
  if (activeViewId === ANIMATION_BLOCK_VIEW_ID) return 0;
  return activeViewId > 0 ? activeViewId : null;
}

function parseOscillatorAnchor(raw: unknown): OscillatorAnchor | null {
  return typeof raw === 'string' && (OSCILLATOR_ANCHORS as readonly string[]).includes(raw)
    ? (raw as OscillatorAnchor)
    : null;
}

function parseOscillatorMode(raw: unknown): OscillatorMode | null {
  return typeof raw === 'string' && (OSCILLATOR_MODES as readonly string[]).includes(raw)
    ? (raw as OscillatorMode)
    : null;
}

function parseOscillatorAxis(raw: unknown): OscillatorAxis {
  return typeof raw === 'string' && (OSCILLATOR_AXES as readonly string[]).includes(raw)
    ? (raw as OscillatorAxis)
    : 'x';
}

export function parseBlockOscillator(raw: unknown): BlockOscillator | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const mode = parseOscillatorMode(record.mode);
  const anchor = parseOscillatorAnchor(record.anchor);
  const axis = parseOscillatorAxis(record.axis);
  const period = Number(record.period);
  const amplitude = Number(record.amplitude);
  const phaseOffsetRaw = record.phaseOffset;
  const phaseOffset = phaseOffsetRaw === undefined || phaseOffsetRaw === null
    ? 0
    : Number(phaseOffsetRaw);
  if (!mode || !anchor) return null;
  if (!Number.isInteger(period) || period < 1 || period > MAX_OSCILLATOR_PERIOD_TICKS) {
    return null;
  }
  if (
    !Number.isInteger(phaseOffset)
    || phaseOffset < 0
    || phaseOffset > MAX_OSCILLATOR_PERIOD_TICKS
  ) {
    return null;
  }
  if (!Number.isFinite(amplitude) || amplitude < 0 || amplitude > MAX_OSCILLATOR_AMPLITUDE) {
    return null;
  }
  return { mode, axis, anchor, period, phaseOffset, amplitude };
}

export function parseBlockAnimation(raw: unknown): BlockAnimation {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return { ...DEFAULT_BLOCK_ANIMATION, items: [] };
    }
  }
  if (!data || typeof data !== 'object') {
    return { ...DEFAULT_BLOCK_ANIMATION, items: [] };
  }
  const record = data as Record<string, unknown>;
  const sync = record.sync === true;

  if (record.type === 'oscillator') {
    const oscillatorsRaw = Array.isArray(record.oscillators) ? record.oscillators : [];
    const oscillators: BlockOscillator[] = [];
    for (const entry of oscillatorsRaw.slice(0, MAX_OSCILLATORS)) {
      const osc = parseBlockOscillator(entry);
      if (osc) oscillators.push(osc);
    }
    return { type: 'oscillator', sync, oscillators };
  }

  const itemsRaw = Array.isArray(record.items) ? record.items : [];
  const items: SequenceAnimationItem[] = [];
  for (const entry of itemsRaw.slice(0, MAX_SEQUENCE_ITEMS)) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    const viewId = Number(item.viewId);
    const ticks = Number(item.ticks);
    if (!Number.isInteger(viewId) || viewId < 1) continue;
    if (!Number.isInteger(ticks) || ticks < 0 || ticks > MAX_SEQUENCE_ITEM_TICKS) continue;
    items.push({ viewId, ticks });
  }
  const type = record.type === 'parametric' ? 'parametric' : 'sequence';
  return { type, sync, items };
}

/** Unit-cube anchor position for an oscillator. */
export function oscillatorAnchorPoint(anchor: OscillatorAnchor): [number, number, number] {
  switch (anchor) {
    case 'center':
      return [0.5, 0.5, 0.5];
    case 'bottom':
      return [0.5, 0, 0.5];
    case 'top':
      return [0.5, 1, 0.5];
    case 'north':
      return [0.5, 0.5, 0];
    case 'south':
      return [0.5, 0.5, 1];
    case 'east':
      return [1, 0.5, 0.5];
    case 'west':
      return [0, 0.5, 0.5];
  }
}

/**
 * Wave in [-1, 1] for the given oscillator at `phaseTick` (already sync/unsync adjusted).
 */
export function oscillatorWave(oscillator: BlockOscillator, phaseTick: number): number {
  const period = Math.max(1, oscillator.period);
  const offset = Number.isFinite(oscillator.phaseOffset) ? oscillator.phaseOffset : 0;
  const phase = (((phaseTick + offset) % period) + period) % period;
  return Math.sin((phase / period) * Math.PI * 2);
}

/**
 * Apply one oscillator to a unit-cube point with a precomputed wave in [-1, 1].
 * Anchor stays fixed; displacement grows linearly with distance from the anchor.
 */
export function applyOscillatorWithWave(
  x: number,
  y: number,
  z: number,
  oscillator: BlockOscillator,
  wave: number,
): [number, number, number] {
  const [ax, ay, az] = oscillatorAnchorPoint(oscillator.anchor);
  const dx = x - ax;
  const dy = y - ay;
  const dz = z - az;
  const dist = Math.hypot(dx, dy, dz);
  if (dist < 1e-8) return [x, y, z];

  const amp = oscillator.amplitude * wave;
  const axis = oscillator.axis;

  switch (oscillator.mode) {
    case 'skew': {
      // Offset only along the chosen axis; amount ∝ distance from the anchor
      // in the plane perpendicular to that axis (anchor stays fixed).
      const driver = axis === 'x'
        ? Math.hypot(dy, dz)
        : axis === 'y'
          ? Math.hypot(dx, dz)
          : Math.hypot(dx, dy);
      if (driver < 1e-8) return [x, y, z];
      const offset = amp * driver;
      if (axis === 'x') return [x + offset, y, z];
      if (axis === 'y') return [x, y + offset, z];
      return [x, y, z + offset];
    }
    case 'rotate': {
      // Rotate around the chosen axis through the anchor; angle ∝ distance.
      const angle = amp * dist * Math.PI;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      if (axis === 'x') {
        const ry = dy * cos - dz * sin;
        const rz = dy * sin + dz * cos;
        return [x, ay + ry, az + rz];
      }
      if (axis === 'y') {
        const rx = dx * cos - dz * sin;
        const rz = dx * sin + dz * cos;
        return [ax + rx, y, az + rz];
      }
      const rx = dx * cos - dy * sin;
      const ry = dx * sin + dy * cos;
      return [ax + rx, ay + ry, z];
    }
    case 'scale': {
      // Stretch / squash only along the chosen axis from the anchor.
      const scale = 1 + amp * dist;
      if (axis === 'x') return [ax + dx * scale, y, z];
      if (axis === 'y') return [x, ay + dy * scale, z];
      return [x, y, az + dz * scale];
    }
  }
}

/**
 * Apply one oscillator to a unit-cube point. Anchor stays fixed; displacement
 * grows linearly with distance from the anchor. Each oscillator acts on a
 * single axis — stack oscillators for multi-axis motion.
 */
export function applyOscillator(
  x: number,
  y: number,
  z: number,
  oscillator: BlockOscillator,
  phaseTick: number,
): [number, number, number] {
  return applyOscillatorWithWave(
    x, y, z, oscillator, oscillatorWave(oscillator, phaseTick),
  );
}

/** Compose all oscillators in order onto a unit-cube point. */
export function applyOscillators(
  x: number,
  y: number,
  z: number,
  oscillators: readonly BlockOscillator[],
  phaseTick: number,
): [number, number, number] {
  let px = x;
  let py = y;
  let pz = z;
  for (const oscillator of oscillators) {
    [px, py, pz] = applyOscillator(px, py, pz, oscillator, phaseTick);
  }
  return [px, py, pz];
}

/**
 * Compose oscillators using precomputed waves + anchors (one sin / tick,
 * not per vertex). `anchors` is xyz triples. Writes out[outOffset..+2].
 */
export function applyOscillatorsWithWavesTo(
  x: number,
  y: number,
  z: number,
  oscillators: readonly BlockOscillator[],
  waves: ArrayLike<number>,
  anchors: ArrayLike<number>,
  out: Float32Array | number[],
  outOffset = 0,
): void {
  let px = x;
  let py = y;
  let pz = z;
  for (let i = 0; i < oscillators.length; i++) {
    const oscillator = oscillators[i]!;
    const wave = waves[i] ?? 0;
    const ai = i * 3;
    const ax = anchors[ai] ?? 0.5;
    const ay = anchors[ai + 1] ?? 0.5;
    const az = anchors[ai + 2] ?? 0.5;
    const dx = px - ax;
    const dy = py - ay;
    const dz = pz - az;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-8) continue;

    const amp = oscillator.amplitude * wave;
    const axis = oscillator.axis;
    switch (oscillator.mode) {
      case 'skew': {
        const driver = axis === 'x'
          ? Math.hypot(dy, dz)
          : axis === 'y'
            ? Math.hypot(dx, dz)
            : Math.hypot(dx, dy);
        if (driver < 1e-8) break;
        const offset = amp * driver;
        if (axis === 'x') px += offset;
        else if (axis === 'y') py += offset;
        else pz += offset;
        break;
      }
      case 'rotate': {
        const angle = amp * dist * Math.PI;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        if (axis === 'x') {
          py = ay + dy * cos - dz * sin;
          pz = az + dy * sin + dz * cos;
        } else if (axis === 'y') {
          px = ax + dx * cos - dz * sin;
          pz = az + dx * sin + dz * cos;
        } else {
          px = ax + dx * cos - dy * sin;
          py = ay + dx * sin + dy * cos;
        }
        break;
      }
      case 'scale': {
        const scale = 1 + amp * dist;
        if (axis === 'x') px = ax + dx * scale;
        else if (axis === 'y') py = ay + dy * scale;
        else pz = az + dz * scale;
        break;
      }
    }
  }
  out[outOffset] = px;
  out[outOffset + 1] = py;
  out[outOffset + 2] = pz;
}

export function isOscillatorAnimation(
  animation: BlockAnimation | null | undefined,
): animation is BlockOscillatorAnimation {
  return animation?.type === 'oscillator';
}

export function sequenceAnimationPeriod(
  animation: Pick<BlockSequenceAnimation, 'items'>,
): number {
  let period = 0;
  for (const item of animation.items) period += Math.max(0, item.ticks);
  return period;
}

/** First playable animation step view id (ticks > 0), else first listed id. */
export function firstSequenceViewId(
  animation: BlockAnimation | null | undefined,
): number | null {
  if (
    !animation
    || (animation.type !== 'sequence' && animation.type !== 'parametric')
    || animation.items.length === 0
  ) {
    return null;
  }
  for (const item of animation.items) {
    if (item.viewId >= 1 && item.ticks > 0) return item.viewId;
  }
  return animation.items.find((item) => item.viewId >= 1)?.viewId ?? null;
}

/**
 * Snapshot id for static UI thumbnails / inventory previews when the definition
 * is in Animation mode. Null means use the denormalized definition fields.
 */
export function thumbnailViewId(
  activeViewId: number,
  animation: BlockAnimation | null | undefined,
): number | null {
  if (activeViewId !== ANIMATION_BLOCK_VIEW_ID) return null;
  return firstSequenceViewId(animation);
}

/**
 * Snapshot id for the current sequence frame, or null when the sequence is empty.
 */
export function resolveSequenceViewId(
  animation: BlockSequenceAnimation,
  worldTick: number,
  animOrigin: number,
): number | null {
  const period = sequenceAnimationPeriod(animation);
  if (period <= 0) return null;
  const phase = animation.sync
    ? ((worldTick % period) + period) % period
    : (((worldTick - animOrigin) % period) + period) % period;
  let cursor = 0;
  for (const item of animation.items) {
    if (item.ticks <= 0) continue;
    cursor += item.ticks;
    if (phase < cursor) return item.viewId;
  }
  return null;
}

/** Current parametric blend segment (from → to over `t` in 0..1). */
export function resolveParametricSegment(
  animation: BlockParametricAnimation,
  worldTick: number,
  animOrigin: number,
): { fromViewId: number; toViewId: number; t: number } | null {
  const playable = animation.items.filter(
    (item) => item.viewId >= 1 && item.ticks > 0,
  );
  if (playable.length === 0) return null;
  if (playable.length === 1) {
    const only = playable[0]!;
    return { fromViewId: only.viewId, toViewId: only.viewId, t: 0 };
  }
  const period = sequenceAnimationPeriod({ items: playable });
  if (period <= 0) return null;
  const phase = animation.sync
    ? ((worldTick % period) + period) % period
    : (((worldTick - animOrigin) % period) + period) % period;
  let cursor = 0;
  for (let index = 0; index < playable.length; index++) {
    const item = playable[index]!;
    const next = playable[(index + 1) % playable.length]!;
    const start = cursor;
    cursor += item.ticks;
    if (phase < cursor) {
      return {
        fromViewId: item.viewId,
        toViewId: next.viewId,
        t: (phase - start) / item.ticks,
      };
    }
  }
  return null;
}

/** Effective snapshot/follow id used when drawing a placement. */
export function resolvePlacementDisplayViewId(
  activeViewId: number,
  animation: BlockAnimation | null | undefined,
  placementViewId: number,
  animOrigin: number,
  worldTick: number,
): number {
  if (placementViewId > RANDOM_BLOCK_VIEW_ID) return placementViewId;
  if (activeViewId !== ANIMATION_BLOCK_VIEW_ID || !animation) {
    return RANDOM_BLOCK_VIEW_ID;
  }
  if (animation.type === 'sequence') {
    return resolveSequenceViewId(animation, worldTick, animOrigin)
      ?? RANDOM_BLOCK_VIEW_ID;
  }
  if (animation.type === 'parametric' || animation.type === 'oscillator') {
    // Sentinel: renderer deforms / blends; not a real snapshot id.
    return ANIMATION_BLOCK_VIEW_ID;
  }
  return RANDOM_BLOCK_VIEW_ID;
}

export function isParametricAnimation(
  animation: BlockAnimation | null | undefined,
): animation is BlockParametricAnimation {
  return animation?.type === 'parametric';
}

/** Pick a snapshot id when the definition is in Random mode; otherwise follow-active (0). */
export function resolvePlaceViewId(
  activeViewId: number,
  views: ReadonlyArray<{ id: number }>,
): number {
  if (activeViewId !== RANDOM_BLOCK_VIEW_ID) return RANDOM_BLOCK_VIEW_ID;
  if (views.length === 0) return RANDOM_BLOCK_VIEW_ID;
  const index = Math.floor(Math.random() * views.length);
  return views[index]!.id;
}

export type ServerMsg =
  | {
      t: 'init';
      selfId: string;
      /** Authoritative spawn pose for the local player (not included in `players`). */
      self: PlayerState;
      players: PlayerState[];
      zones: ZoneState[];
      blocks: BlockTuple[];
      customBlocks: CustomBlockDefinition[];
      libraryBlocks: LibraryBlockDefinition[];
    }
  | { t: 'join'; player: PlayerState }
  | { t: 'leave'; id: string }
  | { t: 'move'; id: string; x: number; y: number; z: number; yaw: number; pitch: number }
  | { t: 'place'; x: number; y: number; z: number; type: number; by: string }
  | { t: 'remove'; x: number; y: number; z: number; by: string }
  | {
      t: 'batch';
      edits: BlockEditTuple[];
      by: string;
      customBlocks?: CustomBlockDefinition[];
      libraryBlocks?: LibraryBlockDefinition[];
    }
  | { t: 'customBlock'; definition: CustomBlockDefinition }
  | { t: 'libraryBlock'; definition: LibraryBlockDefinition }
  | { t: 'zone'; index: number; ownerId: string | null; ownerNick: string | null; cleared: boolean }
  | { t: 'error'; message: string };
